import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { setAuthTokenGetter } from '../services/api';

// Importar AWS Amplify
const amplifyModule = require('aws-amplify');
const authModule = require('aws-amplify/auth');

const { Amplify } = amplifyModule;
const { signIn, signOut, getCurrentUser, fetchAuthSession, confirmSignIn } = authModule;

// Configurar Amplify
const amplifyConfig = {
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
      userPoolClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID,
      region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
    }
  }
};

Amplify.configure(amplifyConfig);

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requiresNewPassword, setRequiresNewPassword] = useState(false);

  useEffect(() => {
    // Configurar el token getter para el servicio de API
    setAuthTokenGetter(getAuthToken);
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      setLoading(true);
      
      const currentUser = await getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        await loadUserProfile(currentUser.signInDetails?.loginId || currentUser.username);
      }
    } catch (error) {
      setUser(null);
      setUserProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const loadUserProfile = async (email) => {
    try {
      const encodedEmail = encodeURIComponent(email);
      const response = await api.get(`/users/email/${encodedEmail}`);
      setUserProfile(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        await createUserProfile(email);
      } else {
        console.error('Error loading user profile:', error);
      }
    }
  };

  const createUserProfile = async (email, additionalData = {}) => {
    try {
      const userData = {
        email: email,
        username: additionalData.username || email.split('@')[0],
        agency_id: additionalData.agency_id || null,
        agent_photo: additionalData.agent_photo || null
      };

      const response = await api.post('/auth/user', userData);
      setUserProfile(response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  };

  const login = async (email, password, newPassword = null) => {
    try {
      setLoading(true);
      setError(null);

      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password: password,
        options: newPassword ? { 
          challengeResponse: newPassword 
        } : undefined
      });

      if (isSignedIn) {
        const currentUser = await getCurrentUser();
        setUser(currentUser);
        setRequiresNewPassword(false);
        await loadUserProfile(email);
        return { success: true };
      } else if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setRequiresNewPassword(true);
        return { 
          success: false, 
          requiresNewPassword: true,
          nextStep 
        };
      } else {
        return { success: false, nextStep };
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const completeNewPassword = async (newPassword) => {
    try {
      setLoading(true);
      setError(null);

      const { isSignedIn } = await confirmSignIn({
        challengeResponse: newPassword
      });

      if (isSignedIn) {
        const currentUser = await getCurrentUser();
        setUser(currentUser);
        setRequiresNewPassword(false);
        const email = currentUser.signInDetails?.loginId || currentUser.username;
        await loadUserProfile(email);
        return { success: true };
      }

      return { success: false };
    } catch (error) {
      console.error('Complete new password error:', error);
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await signOut();
      setUser(null);
      setUserProfile(null);
      setError(null);
    } catch (error) {
      console.error('Logout error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateUserProfile = async (updates) => {
    try {
      if (!userProfile) return;

      const response = await api.put(`/users/${userProfile.id}`, updates);
      setUserProfile(response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  const getAuthToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    error,
    requiresNewPassword,
    login,
    logout,
    completeNewPassword,
    updateUserProfile,
    getAuthToken,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
